import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDebouncedResizeObserver } from '~/shared/hooks';
import { downloadHeatmapData } from '~/shared/services/api';
import styled from 'styled-components';
import { Paper, Fab, Box, BoxProps } from '@mui/material';
import FilterBar from './FilterBar';
import { AlertBar, Loader, TextWithTooltip } from '~/shared/components';
import { useNavAndFilters, useHeatmapData, useScroller } from '~/shared/services/charts';
import ExpandLess from '@mui/icons-material/ExpandLess';
import IndicatorLineChart from './Indicator/IndicatorLineChart';
import IndicatorStackedAreaChart from './Indicator/IndicatorStackedAreaChart';
import { tooltipCfg } from '~/shared/config/tooltipCfg';
import { ReactComponent as MenuIcon } from '~/shared/assets/three_dots.svg';
import { utils, writeFileXLSX } from 'xlsx';

type ImgProps = {
  alt: string;
  src: string;
};

export const Img = (props: BoxProps & ImgProps) => <Box component="img" {...props} />;

const ScrollWrap = styled.div`
  flex: 1;
  overflow-y: scroll;
  height: 100%;
  max-width: 100%;
  ${({ theme }: any) => `
    padding: ${theme.spacing(3)}px;
  `};
`;

const VideoPlayerContainer = styled.div`
  display: flex;
  justify-content: center;
  position: relative;
  height: 100%;
  text-align: center;
`;

const MainContainer = styled(Paper)`
  max-width: 100%;
  display: flex;
  flex-direction: column;
`;

const ContentContainer = styled.div`
  width: 100%;
  display: flex;
  padding: 4px;
`;

const HeatmapContainer = styled.div`
  flex: 1;
  flex-direction: column;
  position: relative;
  padding: 8px;
`;

const FabContainer = styled.div`
  position: relative;
  button {
    position: absolute;
    min-height: 26px;
    height: 26px;
    width: 26px;
    right: 2px;
    bottom: 2px;
    background: var(--background-chart-fab);
    color: var(--color-chart-fab);
  }
`;

const AlignedLoader = styled(Loader)`
  position: fixed;
  left: calc(50% - 25px);
  top: calc(50% - 25px);
`;

const HeaderWrapper = styled('p')`
  min-height: 70px;
  padding: 14px 24px 0 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 100%;
`;

const Header = styled.div`
  height: 30px;
  color: #4c5e72;
  font-size: 18px;
  font-weight: 500;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
`;

const MenuIconWrapper = styled.div`
  cursor: pointer;
  margin-right: -14px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  fill: #4c5e72;
  stroke: transparent;
  &:hover {
    background: #d4e5ff;
  }
`;

const MenuOptions = styled.div`
  width: 92%;
  height: 74px;
  cursor: pointer;
  z-index: 1000 !important;
  background-color: var(--table-menu-options-background-color);
  opacity: 1;
  margin: -10px 3% 0 auto;
  direction: ltr;
  position: absolute;
  font-size: 12px;
  top: 58px;
  right: 26px;
  list-style: none;
  border: 1px solid var(--table-menu-options-border-color);
`;

const Options = styled.ul`
  list-style-type: none;
  padding: 0px;
  font-family: Montserrat, sans-serif;
`;

const Items = styled.li`
  &:hover {
    background-color: #d4e5ff;
  }

  cursor: pointer;
  color: var(--table-item-color);
  background: none;
  font-size: 16px;
  transition:
    background 250ms ease 0s,
    color 250ms ease 0s;
  font-family: Montserrat, sans-serif;
  line-height: 20px;
  padding: 9px 5px 5px 12px;
`;

const ImageContainer = styled('p')`
  padding: 0 24px;
`;

// eslint-disable-next-line @typescript-eslint/ban-types
function useHeatmapClick(navHirarchyItem: any, onScroll: Function, filterValues: any) {
  const [indicatorSections, setIndicatorSections] = useState<any[]>([]);
  useEffect(() => {
    setIndicatorSections([]);
  }, [navHirarchyItem, filterValues]);
  const onClick = useCallback(
    (cellInfo: { source: string; topic?: string; symbol?: string }) => {
      setIndicatorSections([]);
      setTimeout(
        () =>
          navHirarchyItem &&
          setIndicatorSections(
            navHirarchyItem.indicators[cellInfo.source].map((indicatorObj: any) => {
              const indicator = indicatorObj[0];
              const type = indicatorObj[1];
              return { indicator, cellInfo, type };
            }),
          ),
        10,
      );
      setTimeout(onScroll, 200);
    },
    [navHirarchyItem, onScroll],
  );

  return { indicatorSections, onClick };
}

export default function ImagePlayerPage({ navHirarchy, gridIndex }: { navHirarchy: any; gridIndex: any }) {
  const { scrollerRef, scrollToTop, scrollToBottom } = useScroller();
  const { category, subcategory, subsubcategory, prism, navHirarchyItem, filterValues, filterValuesID, prismId }: any =
    useNavAndFilters(navHirarchy, gridIndex);
  const { data, error, isLoading } = useHeatmapData(
    category,
    subcategory,
    subsubcategory,
    prism,
    filterValues,
    prismId,
  );

  const title = data?.title || '';

  const { ref: chartSizeRef }: any = useDebouncedResizeObserver(100);
  const { indicatorSections } = useHeatmapClick(navHirarchyItem, scrollToBottom, filterValues);
  const onDownload = () =>
    downloadHeatmapData(category, subcategory, subsubcategory, prism, filterValues, prismId).catch(e => {
      alert(e.message);
    });

  const [openMenu, setOpenMenu] = useState(false);
  const MenuOptionsData = [
    { name: 'View in full screen', id: 1 },
    { name: 'Download PNG', id: 2 },
    // { name: 'Download CSV', id: 3 },
  ];

  const handleMenuClick = useCallback(() => {
    setOpenMenu(!openMenu);
  }, [openMenu]);

  const handleDownloadExcel = useCallback(() => {
    const ws = utils.json_to_sheet(data);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, 'Data');
    writeFileXLSX(wb, `${title}.xlsx`);
  }, [title, data]);

  const handleDownloadCSV = useCallback(() => {
    const csv_file = document.getElementById(`csv-downloader-${title}`);
    csv_file!.click();
  }, [title]);

  const ref: any = useRef();
  const elem: any = ref.current;

  const handleDownloadPng = useCallback(() => {
    fetch(data?.url, {
      method: 'get',
      headers: {},
    })
      .then(response => {
        response.arrayBuffer().then(function (buffer: any) {
          const url = window.URL.createObjectURL(new Blob([buffer]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `${title}.png`);
          document.body.appendChild(link);
          link.click();
        });
      })
      .catch(err => {
        // eslint-disable-next-line no-console
        console.log(err);
      });
  }, [data, title]);

  const makeFullscreen = useCallback(() => {
    if (elem.requestFullscreen) {
      elem.requestFullscreen();
    } else if (elem.mozRequestFullScreen) {
      /* Firefox */
      elem.mozRequestFullScreen();
    } else if (elem.webkitRequestFullscreen) {
      /* Chrome, Safari and Opera */
      elem.webkitRequestFullscreen();
    } else if (elem.msRequestFullscreen) {
      /* IE/Edge */
      elem.msRequestFullscreen();
    }
  }, [elem]);

  const handleOptionClick = useCallback(
    (name: string) => {
      switch (name) {
        case 'View in full screen':
          makeFullscreen();
          setOpenMenu(false);
          break;
        case 'Download PNG':
          handleDownloadPng();
          setOpenMenu(false);
          break;
        case 'Download XLS':
          handleDownloadExcel();
          setOpenMenu(false);
          break;

        case 'Download CSV':
          handleDownloadCSV();
          setOpenMenu(false);
      }
    },
    [handleDownloadCSV, handleDownloadExcel, makeFullscreen],
  );

  return (
    <ScrollWrap ref={scrollerRef}>
      {error ? <AlertBar message={(error as unknown as any).message} /> : <div />}
      <MainContainer>
        <ContentContainer>
          <HeatmapContainer ref={chartSizeRef}>
            <HeaderWrapper>
              <Header>
                <TextWithTooltip>{title}</TextWithTooltip>
              </Header>
              <MenuIconWrapper onClick={handleMenuClick}>
                <MenuIcon />
              </MenuIconWrapper>
            </HeaderWrapper>
            {openMenu && (
              <MenuOptions onMouseLeave={() => setOpenMenu(false)}>
                <Options>
                  {MenuOptionsData.map(({ id, name }: { id: number; name: string }) => {
                    return (
                      <Items key={id} onClick={() => handleOptionClick(name)}>
                        {name}
                      </Items>
                    );
                  })}
                </Options>
              </MenuOptions>
            )}
            <VideoPlayerContainer>
              {!data?.url || isLoading ? (
                <AlignedLoader />
              ) : (
                <ImageContainer ref={ref}>
                  <Img
                    component="img"
                    sx={{
                      // height: 400,
                      width: '100%',
                      // maxHeight: { xs: 233, md: 167 },
                      // maxWidth: { xs: 350, md: 250 },
                    }}
                    alt={data?.title}
                    src={data?.url}
                  />
                </ImageContainer>
              )}
            </VideoPlayerContainer>
          </HeatmapContainer>
          {!gridIndex && gridIndex !== 0 && (
            <FilterBar
              type={navHirarchyItem?.type}
              filters={navHirarchyItem?.filters}
              values={filterValues}
              filterHierarchy={navHirarchyItem?.narrativeFilterTree}
              filterHierarchyValues={filterValuesID}
              onDownload={onDownload}
              tooltipCfg={tooltipCfg}
            />
          )}
        </ContentContainer>
      </MainContainer>
      {indicatorSections.map((section: any) => (
        <div key={section.indicator + section.topic + section.symbol}>
          {section.type === 'bar' && (
            <IndicatorStackedAreaChart
              key={section.indicator + section.topic + section.symbol}
              {...{ category, prism, prismId, ...section, filterValues, tooltipCfg }}
            ></IndicatorStackedAreaChart>
          )}
          {section.type === 'line' && (
            <IndicatorLineChart
              key={section.indicator + section.topic + section.symbol}
              {...{ category, prism, prismId, ...section, filterValues, tooltipCfg }}
            ></IndicatorLineChart>
          )}
        </div>
      ))}
      <FabContainer>
        {indicatorSections.length > 0 && (
          <Fab onClick={scrollToTop}>
            <ExpandLess />
          </Fab>
        )}
      </FabContainer>
    </ScrollWrap>
  );
}
